import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';

import {ViewChild, Component, OnInit} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {TranslocoService} from '@jsverse/transloco';

import {ActivePromotionConfig} from '../config/active-promotion.config';
import {DataService, OrientDBCollectionView, ORole, OUser} from '@cosbeni/database';
import {ROLES} from '../services/roles';

/**
 * 買取屋 ユーザ管理 (旧 kaitoriya.app /admin `cj-admin-user` を業務ライン別に移植)。
 *
 * 買取屋管理者が「買取屋ユーザ / 買取屋管理者」の OUser を作成・編集・パスワード変更する。
 * 旧画面は既定ロールを writer 固定にしていたが、当画面では割当可能ロールを
 * ROLES.KAITORIYA_USER / KAITORIYA_ADMIN の 2 つに限定する。
 *
 * 保存は OUser を dataService.update([user]) で upsert (name/status/password/roles)、
 * パスワード変更は UPDATE <rid> SET password コマンド。ng2-validators は当アプリ未導入の
 * ため、パスワード一致等の検証はテンプレート駆動 + コンポーネントのゲッターで行う。
 */
@Component({
    standalone: false,
    selector: 'app-kaitoriya-user',
    templateUrl: 'user-management.component.html',
})
export class KaitoriyaUserManagementComponent implements OnInit {
    readonly: Boolean = true;
    @ViewChild('grid') grid: wjcGrid.FlexGrid;
    users: OrientDBCollectionView;
    /** 割当可能ロール (買取屋ユーザ / 買取屋管理者) のみ。 */
    assignableRoles: ORole[] = [];
    private allRoles: ORole[] = [];
    user: any = new OUser();
    selectedRoleName: string = ROLES.KAITORIYA_USER;
    password: string = '';
    confirmPassword: string = '';
    newPassword: string = '';
    confirmNewPassword: string = '';
    mode: string = 'view';

    /** この画面が割当できる買取屋ラインのロール名。 */
    private readonly kaitoriyaRoleNames: string[] = [ROLES.KAITORIYA_USER, ROLES.KAITORIYA_ADMIN];

    constructor(public dataService: DataService, public toasterService: ToasterService, private transloco: TranslocoService) {
        this.users = new OrientDBCollectionView(dataService, ActivePromotionConfig.DATABASE, 'SELECT FROM OUser', 'OUser', '', 0, 100, true, true, false);
        this.users.url = ActivePromotionConfig.SERVER_URL;
        this.users.tokenName = ActivePromotionConfig.DATABASE + '_token';
        // 一覧を買取屋ラインのユーザ (買取屋ユーザ / 買取屋管理者) のみに限定する。
        this.users.filterSpecial = 'roles CONTAINS (name IN [\'' + ROLES.KAITORIYA_USER + '\',\'' + ROLES.KAITORIYA_ADMIN + '\'])';
        this.users.filterOnServer = true;
    }

    ngOnInit(): void {
        this.users.currentChanged.addHandler(this.onCurrentChanged, this);
        this.dataService.query({sql: 'SELECT FROM ORole', count: 'SELECT FROM ORole'}, ActivePromotionConfig.SERVER_URL).subscribe(response => {
            this.allRoles = wjcCore.isArray(response.results) ? response.results : [];
            this.assignableRoles = this.allRoles.filter(role => this.kaitoriyaRoleNames.indexOf(role.name) >= 0);
        });
    }

    onCurrentChanged(sender: any, args: wjcCore.EventArgs): void {
        if (this.users.currentItem != null) {
            this.user = Object.assign({}, this.users.currentItem);
            // 既存ユーザが買取屋ロールを持っていればそれを選択、無ければ既定。
            this.selectedRoleName = ROLES.KAITORIYA_USER;
            if (wjcCore.isArray(this.user.roles)) {
                for (const role of this.user.roles) {
                    if (role && this.kaitoriyaRoleNames.indexOf(role.name) >= 0) {
                        this.selectedRoleName = role.name;
                    }
                }
            }
            this.mode = 'view';
        }
    }

    /** 選択中のロール名に対応する ORole ドキュメントを返す。 */
    private roleFor(name: string): ORole {
        for (const role of this.allRoles) {
            if (role.name === name) {
                return role;
            }
        }
        return null;
    }

    editUser(): void {
        this.mode = 'edit';
    }

    confirmUser(): void {
        this.mode = 'confirm';
    }

    createUser(): void {
        this.mode = 'edit';
        this.user = new OUser();
        this.selectedRoleName = ROLES.KAITORIYA_USER;
        this.password = '';
        this.confirmPassword = '';
    }

    get isNew(): boolean {
        return this.user && this.user['@rid'] == '#-1:-1';
    }

    get createValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.user.name) && this.user.name.length >= 3
            && !wjcCore.isNullOrWhiteSpace(this.password) && this.password.length >= 3
            && this.password === this.confirmPassword
            && !wjcCore.isNullOrWhiteSpace(this.selectedRoleName);
    }

    get editValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.user.name) && this.user.name.length >= 3
            && !wjcCore.isNullOrWhiteSpace(this.selectedRoleName);
    }

    get passwordValid(): boolean {
        return !wjcCore.isNullOrWhiteSpace(this.newPassword) && this.newPassword.length >= 3
            && this.newPassword === this.confirmNewPassword;
    }

    saveUser(): void {
        const role = this.roleFor(this.selectedRoleName);
        if (role != null) {
            this.user.roles = [role];
        }
        if (this.isNew) {
            this.user.password = this.password;
        }
        this.dataService.update([this.user], ActivePromotionConfig.SERVER_URL).subscribe((response: any) => {
            this.mode = 'view';
            this.toasterService.pop('info', this.transloco.translate('common.save'), this.transloco.translate('kaitoriya.saved_message'));
            this.users.queryData();
        }, (error: any) => {
            this.toasterService.pop('error', this.transloco.translate('common.save'), this.transloco.translate('kaitoriya.failed_message'));
        });
    }

    changePassword(): void {
        let command = 'UPDATE  ' + this.user['@rid'] + ' SET password = \'' + this.newPassword + '\'';
        this.dataService.command({sql: command}, ActivePromotionConfig.SERVER_URL).subscribe((response: any) => {
            this.mode = 'view';
            this.newPassword = '';
            this.confirmNewPassword = '';
            this.toasterService.pop('info', this.transloco.translate('kaitoriya.change_password'), this.transloco.translate('kaitoriya.changed_message'));
            this.users.queryData();
        }, (error: any) => {
            this.toasterService.pop('error', this.transloco.translate('kaitoriya.change_password'), this.transloco.translate('kaitoriya.failed_message'));
        });
    }
}
